import { Box, Tooltip, Typography } from '@mui/material';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import { CHART_COLORS } from 'lib/functions/const/colors';
import React, { useRef } from 'react';

interface CentralPieChartProps {
  data: {
    name: string;
    value: number;
  }[];
  colors?: string[];
  height?: number;
  showLegend?: boolean;
  formatYAxis?: (value: number) => string;
  yAxisFormat?: 'K' | 'M' | 'MM' | 'auto';
  innerRadius?: number;
  outerRadius?: number;
}

const CentralPieChart: React.FC<CentralPieChartProps> = ({
  data,
  colors = Object.values(CHART_COLORS),
  height = 300,
  showLegend = true,
  formatYAxis,
  yAxisFormat = 'auto',
  innerRadius = 80,
  outerRadius = 130,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);

  const getFormatFunction = () => {
    if (formatYAxis) return formatYAxis;

    switch (yAxisFormat) {
      case 'K':
        return (value: number) => {
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
      case 'M':
        return (value: number) => {
          if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
      case 'MM':
        return (value: number) => {
          if (value >= 1000000000) {
            const mmValue = value / 1000000000;
            return `${mmValue.toFixed(1)}MM`;
          }
          if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
      case 'auto':
      default:
        return (value: number) => {
          if (value >= 1000000000) {
            const mmValue = value / 1000000000;
            return `${mmValue.toFixed(1)}MM`;
          }
          if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
    }
  };

  const finalFormatYAxis = getFormatFunction();

  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  const valueFormatter = (object: { value: number }) => {
    return `${finalFormatYAxis(object.value)} (${((object.value / totalValue) * 100).toFixed(1)}%)`;
  };

  const chartData = data.map((item, index) => ({
    id: item.name,
    label: item.name,
    value: item.value,
    color: colors[index % colors.length],
  }));

  // Calcular radios proporcionales al height para espacios pequeños
  const chartHeight = height || 300;
  const scaleFactor = chartHeight / 300; // Factor de escala basado en height
  const scaledInnerRadius = innerRadius * scaleFactor;
  const scaledOuterRadius = outerRadius * scaleFactor;

  return (
    <Box ref={elementRef} sx={{ width: '100%' }}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        position="relative"
      >
        <MuiPieChart
          series={[
            {
              data: chartData,
              innerRadius: scaledInnerRadius,
              outerRadius: scaledOuterRadius,
              paddingAngle: 2,
              cornerRadius: 4,
              highlightScope: { fade: 'global', highlight: 'item' },
              faded: {
                innerRadius: scaledInnerRadius,
                additionalRadius: -10,
                color: 'gray',
              },
              valueFormatter,
            },
          ]}
          height={chartHeight}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          slotProps={{ legend: { hidden: true } }}
        />
        {totalValue > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#555555',
                fontSize: `${Math.max(1.2, 1.5 * scaleFactor)}rem`,
                lineHeight: 1,
              }}
            >
              {finalFormatYAxis(totalValue)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666666',
                fontSize: `${Math.max(0.7, 0.8 * scaleFactor)}rem`,
                mt: 0.5,
              }}
            >
              Total
            </Typography>
          </Box>
        )}
      </Box>

      {showLegend && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            mt: 1.5,
            px: 0.5,
          }}
        >
          {chartData.map((item) => (
            <Tooltip
              key={item.id}
              title={`${item.label}: ${finalFormatYAxis(item.value)}`}
              arrow
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  p: '4px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    backgroundColor: item.color,
                    borderRadius: '2px',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666666',
                    fontSize: '11px',
                    fontWeight: 500,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#555555',
                    fontSize: '11px',
                    fontWeight: 600,
                    flexShrink: 0,
                    ml: 'auto',
                  }}
                >
                  {finalFormatYAxis(item.value)}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CentralPieChart;
